import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { useMemo, useCallback } from 'react';
import { api } from '@/services/api';
import { Product, ProductFilters, PaginatedResponse } from '@/types';

// Mock verileri development için opsiyonel tutuyoruz
// Gerçek API bağlanana kadar veya hata durumunda fallback olarak kullanılabilir


export const useProducts = () => {
  // Filtreli, sayfalı ürün listesi
  const useProductList = (filters: ProductFilters = {}) => {
    // Query string oluştur (SWR key olarak kullanılır)
    const queryKey = useMemo(() => {
      const params = new URLSearchParams();
      if (filters.categoryId) params.set('categoryId', filters.categoryId);
      if (filters.categorySlug) params.set('categorySlug', filters.categorySlug);
      if (filters.featured !== undefined) params.set('featured', String(filters.featured));
      if (filters.search) params.set('search', filters.search);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      const query = params.toString();
      return `/products/visible${query ? `?${query}` : ''}`;
    }, [filters]);

    const { data, error, isLoading } = useSWR<PaginatedResponse<Product>>(
      queryKey,
      () => api.products.getVisible(filters),
      {
        keepPreviousData: true, // Sayfa geçişlerinde flicker'ı önler
        onError: (err) => {
          console.error('Failed to fetch products:', err);
        }
      }
    );

    return {
      products: data?.data || [],
      meta: data?.meta,
      isLoading,
      isError: error,
    };
  };

  // Tekil ürün detayı
  const useProductDetail = (slug?: string) => {
    const { data, error, isLoading } = useSWR<Product>(
      slug ? `/products/${slug}` : null,
      () => api.products.getBySlug(slug!),
      {
        onError: (err) => console.error(`Failed to fetch product ${slug}:`, err)
      }
    );

    return {
      product: data,
      isLoading,
      isError: error,
    };
  };

  // Benzer ürünler
  const useRelatedProducts = (productId?: string, limit = 3) => {
    const { data, error, isLoading } = useSWR<Product[]>(
      productId ? `/products/${productId}/related?limit=${limit}` : null,
      () => api.products.getRelated(productId!, limit)
    );

    return {
      relatedProducts: data || [],
      isLoading,
      isError: error,
    };
  };

  // Infinite scroll ile sayfalı ürün listesi
  const useInfiniteProductList = (filters: Omit<ProductFilters, 'page'> = {}, pageSize = 12) => {
    // Filtre key'i: filtreler değişince sıfırdan başla
    const filterKey = useMemo(() => {
      const params = new URLSearchParams();
      if (filters.categoryId) params.set('categoryId', filters.categoryId);
      if (filters.categorySlug) params.set('categorySlug', filters.categorySlug);
      if (filters.featured !== undefined) params.set('featured', String(filters.featured));
      if (filters.search) params.set('search', filters.search);
      if (filters.limit) params.set('limit', String(filters.limit));
      return params.toString();
    }, [filters]);

    const getKey = useCallback((pageIndex: number, previousPageData: PaginatedResponse<Product> | null) => {
      // İlk sayfa değilse ve önceki sayfada veri yoksa dur
      if (previousPageData && previousPageData.data.length === 0) return null;
      // Toplam sayfayı geçtiyse dur
      if (previousPageData && pageIndex >= previousPageData.meta.totalPages) return null;
      return `infinite:/products/visible?${filterKey}&page=${pageIndex + 1}&limit=${pageSize}`;
    }, [filterKey, pageSize]);

    const { data, error, isLoading, isValidating, size, setSize } = useSWRInfinite<PaginatedResponse<Product>>(
      getKey,
      (key) => {
        // key'den page'i parse et
        const url = new URL(key.replace('infinite:', ''), 'http://dummy');
        const page = Number(url.searchParams.get('page')) || 1;
        return api.products.getVisible({ ...filters, page, limit: pageSize });
      },
      {
        revalidateFirstPage: false,
        revalidateOnFocus: false,
      }
    );

    const products = useMemo(() => {
      if (!data) return [];
      return data.flatMap(page => page.data);
    }, [data]);

    const meta = data?.[0]?.meta;
    const isLoadingMore = isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
    const isEmpty = data?.[0]?.data.length === 0;
    const hasMore = meta ? size < meta.totalPages : false;

    const loadMore = useCallback(() => {
      if (!isValidating && hasMore) {
        setSize(s => s + 1);
      }
    }, [isValidating, hasMore, setSize]);

    return {
      products,
      meta,
      isLoading,
      isLoadingMore: isLoadingMore || isValidating,
      isEmpty,
      hasMore,
      loadMore,
      isError: error,
    };
  };

  return {
    useProductList,
    useInfiniteProductList,
    useProductDetail,
    useRelatedProducts,
  };
};
